/**
 * Settings > Communications > Outage Notifications > Email templates.
 * Edit the subject and body wording of each automatic customer outage email. The branded layout (logo, header badge,
 * details box, footer) is fixed. Placeholders like {{customer_name}} are filled in per customer.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import apiService from '../services/api';

interface Template {
  event: string;
  subject: string;
  body_html: string;
  is_default: boolean;
  default_subject: string;
  default_body_html: string;
}
interface Placeholder { token: string; description: string }

const EVENT_LABELS: Record<string, string> = {
  created: 'New outage',
  planned: 'Planned maintenance',
  update: 'Update',
  resolved: 'Resolved',
};
const EVENT_HELP: Record<string, string> = {
  created: 'Sent when an unplanned outage is created and customers are linked to it.',
  planned: 'Sent when planned maintenance is created.',
  update: 'Sent when staff post an update with "Email customers this update" ticked.',
  resolved: 'Sent when the outage is resolved, to customers who were told about it.',
};

const apiMessage = (e: any, fallback: string) => {
  const detail = e?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};

const OutageEmailTemplatesSettings: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [placeholders, setPlaceholders] = useState<Placeholder[]>([]);
  const [event, setEvent] = useState('created');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<null | 'save' | 'reset' | 'preview' | 'sample'>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ subject: string; html: string } | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const subjectRef = useRef<HTMLInputElement | null>(null);
  const [focused, setFocused] = useState<'subject' | 'body'>('body');

  const current = templates.find((t) => t.event === event);
  const dirty = !!current && (subject !== current.subject || body !== current.body_html);

  const load = useCallback(async (keepEvent?: string) => {
    try {
      const data = await apiService.getOutageEmailTemplates();
      setTemplates(data.templates);
      setPlaceholders(data.placeholders);
      const t = data.templates.find((x: Template) => x.event === (keepEvent || 'created')) || data.templates[0];
      setEvent(t.event);
      setSubject(t.subject);
      setBody(t.body_html);
    } catch (e) {
      setError(apiMessage(e, 'Could not load the email templates.'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const switchEvent = (next: string) => {
    if (dirty && !window.confirm('You have unsaved changes to this email. Discard them?')) return;
    const t = templates.find((x) => x.event === next);
    if (!t) return;
    setEvent(next);
    setSubject(t.subject);
    setBody(t.body_html);
    setError(null);
    setNotice(null);
  };

  const insert = (token: string) => {
    const text = `{{${token}}}`;
    if (focused === 'subject' && subjectRef.current) {
      const el = subjectRef.current;
      const start = el.selectionStart ?? subject.length;
      const end = el.selectionEnd ?? start;
      setSubject(subject.slice(0, start) + text + subject.slice(end));
      return;
    }
    const el = bodyRef.current;
    const start = el?.selectionStart ?? body.length;
    const end = el?.selectionEnd ?? start;
    setBody(body.slice(0, start) + text + body.slice(end));
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(start + text.length, start + text.length); });
  };

  const run = async (kind: NonNullable<typeof busy>, fn: () => Promise<void>) => {
    setBusy(kind);
    setError(null);
    setNotice(null);
    try {
      await fn();
    } catch (e) {
      setError(apiMessage(e, 'Something went wrong.'));
    } finally {
      setBusy(null);
    }
  };

  const save = () => run('save', async () => {
    await apiService.saveOutageEmailTemplate(event, { subject, body_html: body });
    await load(event);
    setNotice('Saved. The next outage email will use this wording.');
  });

  const reset = () => {
    if (!window.confirm('Go back to the built-in wording for this email? Your changes to it will be lost.')) return;
    run('reset', async () => {
      await apiService.resetOutageEmailTemplate(event);
      await load(event);
      setNotice('Reset to the built-in wording.');
    });
  };

  const showPreview = () => run('preview', async () => {
    setPreview(await apiService.previewOutageEmailTemplate(event, { subject, body_html: body }));
  });

  const sendSample = () => run('sample', async () => {
    if (dirty) throw { response: { data: { detail: 'Save your changes first: the sample uses the saved wording.' } } };
    const r = await apiService.sendOutageNotificationSample(event);
    setNotice(`Sample sent to ${r.sent} of ${r.recipients} test recipient(s) (set in the Settings sub-tab).`);
  });

  if (loading) return <Box sx={{ p: 4 }}><CircularProgress /></Box>;

  return (
    <Paper variant="outlined" sx={{ p: 3, maxWidth: 980 }}>
      <Typography variant="h6" gutterBottom>Outage email wording</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Edit what each automatic customer email says. The Sonic header, details box (incident, location, times) and footer are added
        automatically. Write the body as simple HTML: plain <code>&lt;p&gt;</code>, <code>&lt;strong&gt;</code> and <code>&lt;a href&gt;</code> are styled for you,
        and <code>{'{{placeholders}}'}</code> are filled in for each customer.
      </Typography>

      <Tabs value={event} onChange={(_, v) => switchEvent(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        {templates.map((t) => (
          <Tab key={t.event} value={t.event} label={`${EVENT_LABELS[t.event]}${t.is_default ? '' : ' •'}`} />
        ))}
      </Tabs>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        {EVENT_HELP[event]} {current?.is_default ? 'Using the built-in wording.' : '• Customised.'}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      <TextField
        label="Subject"
        fullWidth
        size="small"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        onFocus={() => setFocused('subject')}
        inputRef={subjectRef}
        sx={{ mb: 2 }}
      />
      <TextField
        label="Body"
        fullWidth
        multiline
        minRows={12}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onFocus={() => setFocused('body')}
        inputRef={bodyRef}
        inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
        sx={{ mb: 1 }}
      />

      <Typography variant="subtitle2" sx={{ mt: 1 }}>Placeholders (click to insert into the {focused})</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, my: 1 }}>
        {placeholders.map((p) => (
          <Tooltip key={p.token} title={p.description}>
            <Chip size="small" variant="outlined" label={`{{${p.token}}}`} onClick={() => insert(p.token)} clickable />
          </Tooltip>
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
        <Button variant="contained" onClick={save} disabled={!!busy || !dirty}>{busy === 'save' ? 'Saving…' : 'Save'}</Button>
        <Button variant="outlined" onClick={showPreview} disabled={!!busy}>{busy === 'preview' ? 'Loading…' : 'Preview'}</Button>
        <Button variant="outlined" onClick={sendSample} disabled={!!busy}>{busy === 'sample' ? 'Sending…' : 'Send sample to test recipients'}</Button>
        <Button color="warning" onClick={reset} disabled={!!busy || !!current?.is_default}>Reset to built-in wording</Button>
      </Box>

      <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="md" fullWidth>
        <DialogTitle>Preview: {preview?.subject}</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {preview && <iframe title="Email preview" srcDoc={preview.html} style={{ width: '100%', height: 640, border: 0 }} sandbox="" />}
        </DialogContent>
        <DialogActions><Button onClick={() => setPreview(null)}>Close</Button></DialogActions>
      </Dialog>
    </Paper>
  );
};

export default OutageEmailTemplatesSettings;
